import {useState} from "react";
import {Stack, Typography} from "@mui/material";
import {appStyles} from "../../../../AppStyles.ts";
import type {ProfileFieldDefinition, ProfileFieldValues} from "../../../../sdo/ProfileFieldDefinition.ts";
import ProfileDetailsForm from "../../intel/panels/details/ProfileDetailsForm.tsx";

// TODO: Replace with API call to load ResourceDefinition fields from the server.
// Mirrors desktop DefinitionFormBuilder.buildFormLayout(resourceDefinition).
function buildSampleResourceFields(): ProfileFieldDefinition[] {
    return [
        {key: "DISPLAY", label: "Display", hintText: "Display name for the Resource", position: 1, dataType: "STRING", required: true},
        {key: "TITLE", label: "Title", hintText: "Original publication title", position: 2, dataType: "STRING", required: false},
        {key: "AUTHOR", label: "Author", hintText: "Primary author or contributor", position: 3, dataType: "STRING", required: false},
        {key: "PUBLICATION_DATE", label: "Publication Date", hintText: "Original publication date", position: 4, dataType: "DATE", required: false},
        {
            key: "LANGUAGE",
            label: "Language",
            hintText: "Primary language of the source",
            position: 5,
            dataType: "LIST_ENTRY",
            required: false,
            options: [
                {key: "EN", label: "English"},
                {key: "ES", label: "Spanish"},
                {key: "FR", label: "French"},
                {key: "DE", label: "German"},
                {key: "JA", label: "Japanese"},
                {key: "ZH", label: "Chinese"},
                {key: "AR", label: "Arabic"},
                {key: "OTHER", label: "Other"},
            ],
        },
        {
            key: "CATEGORY",
            label: "Category",
            hintText: "Editorial classification",
            position: 6,
            dataType: "LIST_ENTRY",
            required: false,
            options: [
                {key: "ARTICLE", label: "Article"},
                {key: "REPORT", label: "Report"},
                {key: "BOOK", label: "Book"},
                {key: "WHITEPAPER", label: "Whitepaper"},
                {key: "PRESENTATION", label: "Presentation"},
                {key: "TRANSCRIPT", label: "Transcript"},
            ],
        },
        {key: "PAGE_COUNT", label: "Page Count", hintText: "Total number of pages", position: 7, dataType: "INTEGER", required: false},
        {key: "SUMMARY", label: "Summary", hintText: "Editorial summary or abstract", position: 8, dataType: "TEXT", required: false},
        {key: "ARCHIVED", label: "Archived", hintText: "Marks the resource as archived (read-only)", position: 9, dataType: "BOOLEAN", required: false},
    ];
}

// TODO: Replace with API call. Mirrors desktop UXResourceDetails.readResourceProfile()
// which switches on resourceDefinition.getResourceType() to call the right manager method
// (readEnrichment / readFile / readPage / readRecord).
function buildSampleResourceValues(): ProfileFieldValues {
    return {
        DISPLAY: "Q1 2026 Quarterly Intelligence Report",
        TITLE: "Quarterly Intelligence Report — Q1 2026",
        AUTHOR: "Strategic Analysis Group",
        PUBLICATION_DATE: "2026-04-01",
        LANGUAGE: "EN",
        CATEGORY: "REPORT",
        PAGE_COUNT: "147",
        SUMMARY: "Comprehensive quarterly intelligence brief covering geopolitical trends, emerging threat actors, and analyst-corroborated developments across the monitored regions. Includes appendices on source attribution and confidence scoring methodology.",
        ARCHIVED: "false",
    };
}

function UXResourceDetailsPanel() {
    const [fields] = useState<ProfileFieldDefinition[]>(buildSampleResourceFields);
    const [values] = useState<ProfileFieldValues>(buildSampleResourceValues);

    // Resource Details is read-only on desktop (FXML has visible="false" on Save button,
    // and SettingsFormOptions sets enableEdit=false / defaultEditMode=false). Mirroring that.

    return (
        <Stack sx={appStyles.defaultPanelContainer} direction="column" padding={1} width="100%">
            {/* Title */}
            <Stack direction="row" padding={1} justifyContent="center">
                <Typography sx={appStyles.panelSectionTitle}>Details</Typography>
            </Stack>

            {/* Read-only details display */}
            <Stack direction="column" sx={{...appStyles.defaultPanelCardsWrapper, padding: "16px", alignItems: "stretch"}}>
                <ProfileDetailsForm
                    fields={fields}
                    values={values}
                    editable={false}
                />
            </Stack>
        </Stack>
    );
}

export default UXResourceDetailsPanel;
